import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { Link } from 'react-router-dom';
import {
  Search,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  ChevronRight,
  ArrowRight,
  Check,
  X,
  Upload,
  FileText,
  AlertTriangle,
  Loader,
  Download,
  Save
} from 'lucide-react';

const TokenDistribution = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferAction, setTransferAction] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  // Details modal state
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState(null);
  const [detailsData, setDetailsData] = useState(null);

  // Upload modal states
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [distributionRows, setDistributionRows] = useState([]); // parsed CSV rows
  const [csvErrors, setCsvErrors] = useState([]);
  const [csvStats, setCsvStats] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // (Optional future server validation placeholders)
  const [validationResults, setValidationResults] = useState(null);
  const [isValidating, setIsValidating] = useState(false);

  // Transfers data
  const [tokenTransfers, setTokenTransfers] = useState([]);
  const [isLoadingTransfers, setIsLoadingTransfers] = useState(false);
  const [loadErrorTransfers, setLoadErrorTransfers] = useState(null);

  const fetchTransfers = useCallback(async () => {
    setIsLoadingTransfers(true);
    setLoadErrorTransfers(null);
    try {
      // 1. Get custodian (current user) wallet address
      const walletRes = await api.get('/api/wallets/my-wallet');
      const custodianWallet = walletRes?.data?.wallet?.walletAddress?.toLowerCase();
      if (!custodianWallet) {
        throw new Error('Unable to resolve custodian wallet address');
      }

      // 2. Fetch secondary token transfers (include user & bond details for mapping)
      //    Backend list endpoint does not currently support direct fromWalletAddress filtering
      //    so we filter client-side for now.
      const transfersRes = await api.get('/api/token-transfers', {
        params: {
          transferType: 'Secondary',
          includeUserDetails: true,
          includeBondDetails: true,
          limit: 100 // reasonable page size; adjust if pagination UI added
        }
      });

      const rawList = transfersRes?.data?.data?.data || []; // shape: { success, message, data: { data: [...], pagination } }

      const mapped = rawList
        .filter(t => (t.fromWalletAddress || '').toLowerCase() === custodianWallet)
        .map(t => {
          const normalizeStatus = (status) => {
            switch (status) {
              case 'Pending':
              case 'Pending L1':
              case 'Pending L2':
              case 'Processing':
                return 'pending';
              case 'Approved':
                return 'approved';
              case 'Completed':
                return 'completed';
              case 'Rejected':
                return 'rejected';
              default:
                return 'pending';
            }
          };

          const fromUser = t.fromUserId || {}; // populated user doc
          const toUser = t.toUserId || {};
          const pickName = (u) => u.fullName || [u.firstName, u.lastName].filter(Boolean).join(' ') || u.companyName || u.email || 'Unknown';

          // Ensure numeric formatting: some backends may return Decimal128/objects; coerce safely
          const numericTotalValue = (typeof t.totalValue === 'number')
            ? t.totalValue
            : (t.totalValue && typeof t.totalValue === 'object' && t.totalValue._hex) // ethers BigNumber style
              ? parseInt(t.totalValue._hex, 16)
              : (t.totalValue && t.totalValue.$numberDecimal) // Mongo Decimal128 JSON form
                ? parseFloat(t.totalValue.$numberDecimal)
                : Number(t.totalValue); // fallback (NaN becomes handled below)

          return {
            id: t._id,
            transferId: t.transferId,
            bondName: t.bondId?.bondName || t.stockCode || '—',
            senderName: pickName(fromUser),
            receiverName: pickName(toUser),
            senderWallet: t.fromWalletAddress,
            receiverWallet: t.toWalletAddress,
            tokenAmount: t.tokenAmount,
            transferValue: (numericTotalValue != null && !Number.isNaN(numericTotalValue))
              ? `${numericTotalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${t.currency || ''}`
              : '—',
            status: normalizeStatus(t.status),
            requestDate: t.createdAt,
            reason: t.description || t.referenceNumber || '—',
            complianceChecks: {
              // Placeholder values until backend provides real compliance object
              overallStatus: 'Pass',
              senderBalance: 'N/A',
              receiverKyc: 'N/A',
              transferLimit: 'N/A'
            }
          };
        });

      setTokenTransfers(mapped);
    } catch (e) {
      console.error('Failed to load token transfers', e);
      setLoadErrorTransfers(e?.response?.data?.message || e.message || 'Unknown error');
    } finally {
      setIsLoadingTransfers(false);
    }
  }, []);

  useEffect(() => {
    fetchTransfers();
  }, [fetchTransfers]);

  const filteredTransfers = tokenTransfers.filter(transfer => {
    const matchesSearch = transfer.bondName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      transfer.transferId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      transfer.senderName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || transfer.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const getTransferStatusBadge = (status) => {
    const statusClasses = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return statusClasses[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4" />;
      case 'approved':
      case 'completed':
        return <CheckCircle className="h-4 w-4" />;
      case 'rejected':
        return <XCircle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const getComplianceStatusBadge = (status) => {
    return status === 'Pass' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
  };

  const openDetails = async (transfer) => {
    setShowDetailsModal(true);
    setDetailsLoading(true);
    setDetailsError(null);
    setDetailsData(null);
    try {
      const { data } = await api.get(`/api/token-transfers/${transfer.id}`, {
        params: {
          includeUserDetails: true,
          includeBondDetails: true,
          includeAllocationDetails: true,
          includeAuditTrail: true
        }
      });
      setDetailsData(data?.data || data); // controller wraps under data
    } catch (e) {
      setDetailsError(e?.response?.data?.message || e.message || 'Failed to load details');
    } finally {
      setDetailsLoading(false);
    }
  };

  // ===== CSV Parsing (simplified) =====
  const requiredHeaders = ['bond id', 'investor id', 'investor name', 'token amount', 'whitelisted wallet', 'payment reference'];

  const parseDistributionCSV = (text) => {
    const lines = text.trim().split(/\r?\n/).filter(l => l.trim() !== '');
    if (lines.length === 0) {
      return { data: [], errors: ['CSV file is empty'], stats: { totalRows: 0, validRows: 0, totalAmount: 0 } };
    }
    const headerParts = lines[0].split(',').map(h => h.trim().replace(/"/g, '').toLowerCase());
    // Basic header validation (order flexible but must include all)
    const missing = requiredHeaders.filter(r => !headerParts.includes(r));
    if (missing.length) {
      return { data: [], errors: [`Missing required columns: ${missing.join(', ')}`], stats: { totalRows: 0, validRows: 0, totalAmount: 0 } };
    }
    // Map indices
    const idx = (name) => headerParts.indexOf(name);
    const data = [];
    const errors = [];
    let totalAmount = 0;
    const bondIds = new Set();
    // Track investor occurrences for duplicate detection: { investorId: [rowNumbers...] }
    const investorRowMap = {};
    for (let i = 1; i < lines.length; i++) {
      const raw = lines[i];
      if (!raw.trim()) continue;
      const cols = raw.split(',').map(c => c.trim().replace(/"/g, ''));
      if (cols.length < headerParts.length) {
        errors.push(`Row ${i + 1}: insufficient columns`);
        continue;
      }
      const row = {
        bondId: cols[idx('bond id')] || '',
        investorId: cols[idx('investor id')] || '',
        investorName: cols[idx('investor name')] || '',
        tokenAmountRaw: cols[idx('token amount')] || '',
        wallet: cols[idx('whitelisted wallet')] || '',
        paymentReference: cols[idx('payment reference')] || '',
        rowNumber: i + 1 // preserve original line number (header is row 1)
      };
      const rowErrors = [];
      if (!row.bondId) rowErrors.push('Missing Bond ID'); else bondIds.add(row.bondId);
      if (!row.investorId) rowErrors.push('Missing Investor ID');
      if (!row.investorName) rowErrors.push('Missing Investor Name');
      const amt = parseFloat(row.tokenAmountRaw.replace(/,/g, ''));
      if (isNaN(amt) || amt <= 0) rowErrors.push('Invalid Bond Amount'); else row.tokenAmount = amt;
      if (!row.wallet || row.wallet.length < 8) rowErrors.push('Invalid Wallet');
      if (!row.paymentReference) rowErrors.push('Missing Payment Reference');
      if (rowErrors.length) {
        errors.push(`Row ${i + 1}: ${rowErrors.join('; ')}`);
      } else {
        totalAmount += row.tokenAmount;
        data.push(row);
        // Collect for duplicate detection (only if investorId present and row valid)
        if (row.investorId) {
          const key = row.investorId.trim();
          if (!investorRowMap[key]) investorRowMap[key] = [];
          investorRowMap[key].push(row.rowNumber);
        }
      }
    }
    if (bondIds.size > 1) errors.push(`Multiple Bond IDs detected: ${Array.from(bondIds).join(', ')}`);
    // Detect duplicates: any investorId with more than one occurrence
    Object.entries(investorRowMap).forEach(([invId, rowsArr]) => {
      if (rowsArr.length > 1) {
        errors.push(`Duplicate Investor ID '${invId}' appears in multiple rows (${rowsArr.join(', ')})`);
      }
    });
    if (!data.length) errors.push('No valid data rows');
    return { data, errors, stats: { totalRows: lines.length - 1, validRows: data.length, totalAmount } };
  };

  // Validate Shariah / product type compatibility for parsed rows
  const validateProductCompatibility = async (rows) => {
    if (!rows.length) return [];
    const bondId = rows[0].bondId;
    try {
      // Fetch bond to get productType
      const bondRes = await api.get(`/api/bonds/${bondId}`);
      const productType = bondRes?.data?.data?.productType || bondRes?.data?.productType || bondRes?.data?.data?.bond?.productType;
      if (!productType) {
        return ["Unable to resolve bond product type for compatibility check"];
      }
      // Get custodian on-chain token balance for this bond
      let custodianBalance = 0;
      try {
        const balRes = await api.get(`/api/bonds/${bondId}/custodian-balance`);
        const balInfo = balRes?.data?.data?.balance || balRes?.data?.balance;
        // Prefer aggregated totalBalanceTokens; fall back to balanceTokens
        let rawBal = balInfo?.totalBalanceTokens ?? balInfo?.balanceTokens ?? 0;
        if (typeof rawBal === 'string') {
          const num = parseFloat(rawBal);
          if (!isNaN(num)) rawBal = num; else rawBal = 0;
        }
        custodianBalance = rawBal;
      } catch (balErr) {
        return [balErr?.response?.data?.message || 'Failed to fetch custodian token balance for this bond'];
      }
      // Compare aggregate requested amount vs custodian balance
      const totalRequested = rows.reduce((sum, r) => sum + (r.tokenAmount || 0), 0);
      if (custodianBalance <= 0) {
        return ["Custodian holds zero balance for this bond's token. Upload not permitted."];
      }
      if (totalRequested > custodianBalance) {
        return [`Aggregate token amount ${totalRequested} exceeds custodian on-chain balance ${custodianBalance}`];
      }
      // Fetch unique wallets in parallel (batched) and cache accountType + userId for ownership + compatibility validation
      const uniqueWallets = [...new Set(rows.map(r => r.wallet.toLowerCase()))];
      const walletCache = {}; // { walletLower: { accountType, userId } }
      const walletFetchPromises = uniqueWallets.map(w => (
        api.get(`/api/wallets/address/${w}`)
          .then(wRes => {
            const walletObj = wRes?.data?.wallet || wRes?.data?.data?.wallet || wRes?.data;
            const acctType = walletObj?.bankAccountDetails?.accountType;
            const userIdRaw = walletObj?.userId;
            const userId = typeof userIdRaw === 'string' ? userIdRaw : (userIdRaw?._id || userIdRaw?.id || userIdRaw);
            walletCache[w] = { accountType: acctType || 'unknown', userId: userId ? String(userId) : null };
          })
          .catch(() => {
            walletCache[w] = { accountType: 'unknown', userId: null };
          })
      ));
      // Wait for all wallet fetches to settle
      await Promise.allSettled(walletFetchPromises);
      const compatibilityErrors = [];
      rows.forEach((row, idx) => {
        const walletKey = row.wallet.toLowerCase();
        const walletInfo = walletCache[walletKey] || { accountType: 'unknown', userId: null };
        const acctType = walletInfo.accountType || 'unknown';
        const rowNumber = idx + 2; // header offset
        if (acctType === 'unknown') {
          compatibilityErrors.push(`Row ${rowNumber}: Wallet ${row.wallet} not found or inaccessible for validation`);
          return;
        }
        // Ownership check: wallet must belong to investorId
        if (walletInfo.userId && row.investorId && walletInfo.userId !== String(row.investorId)) {
          compatibilityErrors.push(`Row ${rowNumber}: Wallet ${row.wallet} does not belong to Investor ${row.investorId}`);
        } else if (!walletInfo.userId) {
          compatibilityErrors.push(`Row ${rowNumber}: Cannot determine owner for wallet ${row.wallet}`);
        }
        // Islamic vs Conventional rule: Islamic wallet only accepts Islamic (Sukuk); Conventional accepts both
        if (acctType === 'islamic' && productType !== 'Sukuk') {
          compatibilityErrors.push(`Row ${rowNumber}: Islamic wallet cannot receive non-Islamic product type '${productType}'`);
        }
      });
      return compatibilityErrors;
    } catch (e) {
      return [e?.response?.data?.message || e.message || 'Failed product compatibility validation'];
    }
  };

  // Validate that each investorId belongs to (was createdBy) the current custodian
  const validateInvestorOwnership = async (rows) => {
    if (!rows.length) return [];
    try {
      // Resolve current custodian user id
      const walletRes = await api.get('/api/wallets/my-wallet');
      const custodianUserIdRaw = walletRes?.data?.wallet?.userId;
      const custodianUserId = typeof custodianUserIdRaw === 'string'
        ? custodianUserIdRaw
        : (custodianUserIdRaw?._id || custodianUserIdRaw?.id || custodianUserIdRaw);
      const custodianUserIdStr = String(custodianUserId || '');
      if (!/^[0-9a-fA-F]{24}$/.test(custodianUserIdStr)) {
        return ['Unable to resolve valid custodian user id for ownership validation'];
      }

      const uniqueInvestorIds = [...new Set(rows.map(r => r.investorId).filter(Boolean))];
      const ownershipErrors = [];
      // Separate invalid IDs early
      const validInvestorIds = [];
      uniqueInvestorIds.forEach(invId => {
        if (!/^[0-9a-fA-F]{24}$/.test(invId)) {
          rows.forEach((r, idx) => { if (r.investorId === invId) ownershipErrors.push(`Row ${idx + 2}: Investor ID '${invId}' is not a valid ObjectId`); });
        } else {
          validInvestorIds.push(invId);
        }
      });

      // Fetch all valid investor docs in parallel (Promise.allSettled) to avoid sequential latency
      const investorPromises = validInvestorIds.map(invId => (
        api.get(`/api/users/${invId}`)
          .then(userRes => ({ invId, userRes }))
          .catch(err => ({ invId, error: err }))
      ));
      const investorResults = await Promise.allSettled(investorPromises);
      investorResults.forEach(res => {
        if (res.status === 'fulfilled') {
          const { invId, userRes, error } = res.value;
          if (error) {
            const msg = error?.response?.data?.message || error.message || 'lookup failed';
            rows.forEach((r, idx) => { if (r.investorId === invId) ownershipErrors.push(`Row ${idx + 2}: Investor ${invId} validation error - ${msg}`); });
            return;
          }
          const userData = userRes?.userRes?.data?.data?.user || userRes?.userRes?.data?.data || userRes?.userRes?.data?.user || userRes?.userRes?.data;
          const extracted = userRes?.userRes ? (userRes.userRes.data?.data?.user || userRes.userRes.data?.data || userRes.userRes.data?.user || userRes.userRes.data) : (userRes?.data?.data?.user || userRes?.data?.data || userRes?.data?.user || userRes?.data);
          const finalUser = userData || extracted;
          if (!finalUser) {
            rows.forEach((r, idx) => { if (r.investorId === invId) ownershipErrors.push(`Row ${idx + 2}: Investor ${invId} not found`); });
            return;
          }
          const createdByRaw = finalUser.createdBy;
          const creatorId = typeof createdByRaw === 'string' ? createdByRaw : (createdByRaw?._id || createdByRaw?.id || createdByRaw);
          if (!creatorId) {
            rows.forEach((r, idx) => { if (r.investorId === invId) ownershipErrors.push(`Row ${idx + 2}: Investor ${invId} missing createdBy reference`); });
            return;
          }
          if (String(creatorId) !== custodianUserIdStr) {
            rows.forEach((r, idx) => { if (r.investorId === invId) ownershipErrors.push(`Row ${idx + 2}: Investor ${invId} not created by this custodian`); });
          }
          // Check if user account is active
          const isActive = finalUser.isActive;
          if (isActive === false) {
            rows.forEach((r, idx) => { if (r.investorId === invId) ownershipErrors.push(`Row ${idx + 2}: Investor ${invId} account is not active`); });
          }
        } else {
          const { invId, error } = res.reason || {};
          const inv = invId || 'unknown';
          const msg = error?.response?.data?.message || error?.message || 'lookup failed';
          rows.forEach((r, idx) => { if (r.investorId === inv) ownershipErrors.push(`Row ${idx + 2}: Investor ${inv} validation error - ${msg}`); });
        }
      });
      return ownershipErrors;
    } catch (e) {
      return [e?.response?.data?.message || e.message || 'Failed investor ownership validation'];
    }
  };

  // New validation: ensure CSV token amounts exactly match remaining undistributed custodian allocation capacity
  // Logic:
  // 1. Sum existing custodian allocations for this bond (S_existing)
  // 2. Find custodian's main allocation amount in primary Allocation table (M)
  // 3. Require M > S_existing else error "custodian wallet does not have extra tokens to distribute"
  // 4. Let remaining = M - S_existing; require sum(CSV) == remaining (within tiny epsilon) else error
  const validateCustodianAllocationCapacity = async (rows) => {
    if (!rows.length) return [];
    const errors = [];
    try {
      const bondId = rows[0].bondId;
      // Resolve custodian user info (id + profile) for matching against main allocation row
      const walletRes = await api.get('/api/wallets/my-wallet');
      const custodianUserIdRaw = walletRes?.data?.wallet?.userId;
      const custodianUserId = typeof custodianUserIdRaw === 'string'
        ? custodianUserIdRaw
        : (custodianUserIdRaw?._id || custodianUserIdRaw?.id || custodianUserIdRaw);
      const custodianUserIdStr = custodianUserId ? String(custodianUserId) : '';
      if (!/^[0-9a-fA-F]{24}$/.test(custodianUserIdStr)) {
        return ['Unable to resolve custodian user id for allocation capacity validation'];
      }

      // Fetch detailed user (for email / company names matching)
      let userEmail = '';
      let companyName = '';
      let legalEntityName = '';
      let fullName = '';
      try {
        const userRes = await api.get(`/api/users/${custodianUserIdStr}`);
        const userData = userRes?.data?.data?.user || userRes?.data?.data || userRes?.data?.user || userRes?.data;
        userEmail = userData?.email || '';
        companyName = userData?.corporateInfo?.companyName || '';
        legalEntityName = userData?.corporateInfo?.legalEntityName || '';
        fullName = userData?.fullName || (
          (userData?.personalInfo?.firstName || '') + ' ' + (userData?.personalInfo?.lastName || '')
        ).trim();
      } catch (uErr) {
        // Non-fatal; just fewer identifiers available
        console.warn('User fetch failed for allocation capacity validation:', uErr?.message || uErr);
      }

      // 1. Existing custodian allocations total (S_existing)
      let existingCustodianAllocated = 0;
      try {
        const caRes = await api.get(`/api/custodian/bonds/${bondId}/allocation`);
        existingCustodianAllocated = Number(caRes?.data?.data?.totalAmount) || 0;
      } catch (caErr) {
        if (caErr?.response?.status === 404) {
          existingCustodianAllocated = 0; // no prior allocations
        } else {
          return [caErr?.response?.data?.message || caErr.message || 'Failed to fetch existing custodian allocations'];
        }
      }

      // 2. Fetch main allocation summary for bond and locate custodian's main allocation row(s)
      let mainAllocationAmount = 0;
      try {
        const allocRes = await api.get(`/api/allocations/${bondId}`);
        const summary = allocRes?.data?.data || allocRes?.data;
        const allocs = summary?.allocations || [];
        const candidates = [custodianUserIdStr, userEmail, companyName, legalEntityName, fullName]
          .filter(v => !!v)
          .map(v => v.toLowerCase());
        allocs.forEach(a => {
          const invId = (a.investorId || '').toString().toLowerCase();
          const invName = (a.investorName || '').toString().toLowerCase();
          if (candidates.includes(invId) || candidates.includes(invName)) {
            mainAllocationAmount += Number(a.tokenAmount) || 0;
          }
        });
      } catch (maErr) {
        return [maErr?.response?.data?.message || maErr.message || 'Failed to fetch main allocation summary for bond'];
      }

      if (mainAllocationAmount <= 0) {
        return ['Unable to locate custodian main allocation amount for this bond'];
      }

      // 3. Ensure M > S_existing
      if (!(mainAllocationAmount > existingCustodianAllocated)) {
        return ['custodian wallet does not have extra tokens to distribute'];
      }

      // 4. Validate CSV sum matches remaining
      const csvTotal = rows.reduce((sum, r) => sum + (r.tokenAmount || 0), 0);
      const remaining = mainAllocationAmount - existingCustodianAllocated;
      const epsilon = 1e-6;
      if (Math.abs(csvTotal - remaining) > epsilon) {
        return [
          `CSV total token amount (${csvTotal}) must equal remaining undistributed allocation amount (${remaining})`
        ];
      }

      return errors; // empty => success
    } catch (err) {
      return [err?.response?.data?.message || err.message || 'Failed allocation capacity validation'];
    }
  };

  const resetUploadState = () => {
    setUploadedFile(null);
    setDistributionRows([]);
    setCsvErrors([]);
    setCsvStats(null);
    setShowPreview(false);
    setUploadProgress(0);
    setValidationResults(null);
    setIsValidating(false);
  };

  const handleCsvFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      alert('Please select a .csv file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('File exceeds 5MB limit');
      return;
    }
    resetUploadState();
    setUploadedFile(file);
    setIsUploading(true);
    // Simulate progress
    setUploadProgress(10);
    try {
      const text = await file.text();
      const result = parseDistributionCSV(text);
      setUploadProgress(90);
      if (result.errors.length) setCsvErrors(result.errors);
      setDistributionRows(result.data);
      setCsvStats(result.stats);
      if (result.data.length && result.errors.length === 0) {
        // 1. Investor ownership validation
        setUploadProgress(92);
        const ownershipErrors = await validateInvestorOwnership(result.data);
        if (ownershipErrors.length) {
          setCsvErrors(prev => [...prev, ...ownershipErrors]);
        } else {
          // 2. Product compatibility validation
          setUploadProgress(95);
          const compatErrors = await validateProductCompatibility(result.data);
          if (compatErrors.length) {
            setCsvErrors(prev => [...prev, ...compatErrors]);
          } else {
            // 3. Allocation capacity validation (new requirement)
            setUploadProgress(97);
            const capacityErrors = await validateCustodianAllocationCapacity(result.data);
            if (capacityErrors.length) {
              setCsvErrors(prev => [...prev, ...capacityErrors]);
            } else {
              setShowPreview(true);
            }
          }
        }
      }
      setUploadProgress(100);
    } catch (err) {
      setCsvErrors([`Failed to read file: ${err.message}`]);
    } finally {
      setIsUploading(false);
      e.target.value = '';
    }
  };

  const handleConfirmUpload = async () => {
    if (!distributionRows.length) return;
    try {
      setIsUploading(true);
      // 1. Resolve custodian user id (counterparty for all Buy transfers)
      const walletRes = await api.get('/api/wallets/my-wallet');
      const custodianUserIdRaw = walletRes?.data?.wallet?.userId;
      const custodianUserId = typeof custodianUserIdRaw === 'string'
        ? custodianUserIdRaw
        : (custodianUserIdRaw?._id || custodianUserIdRaw?.id || custodianUserIdRaw);
      if (!custodianUserId) throw new Error('Unable to resolve custodian user ID');
      const custodianUserIdStr = String(custodianUserId);
      if (!/^[0-9a-fA-F]{24}$/.test(custodianUserIdStr)) throw new Error('Custodian user ID is not a valid ObjectId');

      // 2. Fetch bond once (all rows same bond) to obtain nominal value (price per token)
      const bondId = distributionRows[0].bondId;
      let nominalValue = 1;
      try {
        const bondRes = await api.get(`/api/bonds/${bondId}`);
        const bondData = bondRes?.data?.data?.bond || bondRes?.data?.data || bondRes?.data || {};
        nominalValue = bondData.nominalValue || bondData.faceValue || bondData.parValue || bondData?.tokenDetails?.nominalValue || 1;
      } catch (err) {
        throw new Error(`Bond fetch failed: ${err.message}`);
      }

      // 3. Iterate rows creating SECONDARY TRANSFER records (NOT token transfers)
      // Each row -> Buy transfer where client (investor) buys from custodian
      const successes = [];
      const failures = [];
      const nowIso = new Date().toISOString(); // Assumption: tradeDate & valueDate = now
      for (let i = 0; i < distributionRows.length; i++) {
        const r = distributionRows[i];
        const loopProgress = Math.round((i / distributionRows.length) * 80);
        setUploadProgress(10 + loopProgress);
        try {
          const clientIdStr = String(r.investorId).trim();
          if (!/^[0-9a-fA-F]{24}$/.test(clientIdStr)) throw new Error(`Row investorId '${clientIdStr}' invalid`);
          const payload = {
            bondId: r.bondId,
            transferType: 'Buy', // Custodian is seller (counterparty), client receives tokens
            clientId: clientIdStr,
            clientName: r.investorName,
            counterpartyId: custodianUserIdStr,
            counterpartyName: 'Custodian Distribution',
            nominalValueTransferred: r.tokenAmount,
            price: nominalValue,
            tradeDate: nowIso,
            valueDate: nowIso,
            currency: 'MYR',
            paymentReference: r.paymentReference,
            transferReason: `CSV Distribution Upload - Investor ${clientIdStr}`
          };
          const resp = await api.post('/api/secondary-transfers/create', payload);
          const created = resp?.data?.data;
          successes.push({ row: i + 2, transferRequestId: created?.transferRequestId });
        } catch (err) {
          failures.push({ row: i + 2, error: err?.response?.data?.message || err.message });
        }
      }
      setUploadProgress(95);
      setUploadProgress(100);

      if (failures.length === 0) {
        alert(`Created ${successes.length} secondary transfer requests (Total Tokens: ${csvStats.totalAmount}). They now require L1/L2 approval before token transfers are generated.`);
        setShowUploadModal(false);
        resetUploadState();
        // Optional: refresh list (currently shows token transfers; may remain empty until approvals complete)
        fetchTransfers();
      } else if (successes.length === 0) {
        alert(`All ${failures.length} rows failed. First error: Row ${failures[0].row} - ${failures[0].error}`);
      } else {
        const firstFail = failures[0];
        alert(`Created ${successes.length} secondary transfer requests; ${failures.length} failed. Example failure Row ${firstFail.row}: ${firstFail.error}`);
      }
    } catch (e) {
      alert(e.message || 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  const downloadTemplate = () => {
    const csv = 'Bond ID,Investor ID,Investor Name,Bond Amount,Whitelisted Wallet,Payment Reference\n';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'token_distribution_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleTransferAction = (transfer, action) => {
    setSelectedTransfer(transfer);
    setTransferAction(action);
    setShowTransferModal(true);
    setRejectionReason('');
  };

  const confirmTransferAction = () => {
    setShowTransferModal(false);
    // In real implementation, this would call API
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Allocation CSV logic & distribution removed per requirement to drop allocation tab

  // Allocation upload & distribution helpers removed

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}


      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Token Distribution</h1>
            <p className="mt-1 text-gray-600">Review and manage secondary market token distribution requests.</p>
          </div>
          <div className="mt-4 sm:mt-0">
            <button
              onClick={() => { resetUploadState(); setShowUploadModal(true); }}
              className="btn-primary"
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload CSV
            </button>
          </div>
        </div>

        {/* Distribution Section */}
        <div className="space-y-6">
          {/* Filters */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            {/* Search Card - 60% width (3 out of 5 columns) */}
            <div className="card lg:col-span-4">
              <div className="card-body">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-dark-gray w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search by bond, distribution ID, sender..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="form-input pl-10"
                  />
                </div>
              </div>
            </div>

            {/* Status Filter Card - 40% width (2 out of 5 columns) */}
            <div className="card lg:col-span-1">
              <div className="card-body">
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="form-input"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="completed">Completed</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
            </div>
          </div>

          {/* Distribution Table */}
          <div className="bg-white shadow rounded-lg overflow-hidden overflow-x-auto">
            <div className="card">
              <div className="card-header">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-medium text-secondary-900">
                    Token Distribution
                  </h2>

                </div>
              </div>
            </div>
            <table className="table">
              <thead className="table-header">
                <tr>
                  <th className="table-header-cell">Distribution Details</th>
                  <th className="table-header-cell">
                    From → To
                  </th>
                  <th className="table-header-cell">
                    Amount
                  </th>
                  <th className="table-header-cell">
                    Date
                  </th>
                  <th className="table-header-cell">
                    Status
                  </th>
                  <th className="table-header-cell">Details</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTransfers.map((transfer) => (
                  <tr key={transfer.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {transfer.transferId}
                        </div>
                        <div className="text-sm text-gray-500">
                          {transfer.bondName}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="text-sm">
                          <div className="font-medium text-gray-900">{transfer.senderName}</div>
                          <div className="text-gray-500 font-mono text-xs">
                            {transfer.senderWallet.substring(0, 8)}...
                          </div>
                        </div>
                        <ArrowRight className="h-4 w-4 mx-2 text-gray-400" />
                        <div className="text-sm">
                          <div className="font-medium text-gray-900">{transfer.receiverName}</div>
                          <div className="text-gray-500 font-mono text-xs">
                            {transfer.receiverWallet.substring(0, 8)}...
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm">
                        <div className="font-medium text-gray-900">{transfer.tokenAmount} tokens</div>
                        <div className="text-gray-500">{transfer.transferValue}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(transfer.requestDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTransferStatusBadge(transfer.status)}`}>
                        {getStatusIcon(transfer.status)}
                        <span className="ml-1 capitalize">{transfer.status}</span>
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => openDetails(transfer)}
                        className="text-primary-600 hover:text-primary-700 p-1"
                        title="View transfer details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredTransfers.length === 0 && (
              <div className="text-center py-12">
                <ArrowRight className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No token distributions found</h3>
                <p className="text-gray-600">
                  {searchTerm || filterStatus !== 'all'
                    ? 'Try adjusting your search or filter criteria.'
                    : 'No token distribution requests at this time.'
                  }
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Allocation-related modals removed */}

      {/* Upload Distribution CSV Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">Upload Token Distribution CSV</h3>
              <button
                onClick={() => { setShowUploadModal(false); resetUploadState(); }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
              {/* File Upload */}
              <div className="mb-6">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Select CSV File</h4>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                  <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <div className="text-sm text-gray-600 mb-4">
                    <span className="font-medium text-blue-600 hover:text-blue-500 cursor-pointer" onClick={() => document.getElementById('distribution-csv-input')?.click()}>Click to upload</span> or drag and drop
                  </div>
                  <input id="distribution-csv-input" type="file" accept=".csv" className="hidden" onChange={handleCsvFileChange} />
                  <button
                    type="button"
                    onClick={() => document.getElementById('distribution-csv-input')?.click()}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >Choose File</button>
                  <p className="text-xs text-gray-500 mt-2">CSV only • Max 5MB</p>
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md text-left">
                    <h5 className="text-sm font-medium text-blue-800 mb-2">Format Requirements</h5>
                    <ul className="text-xs text-blue-700 space-y-1 list-disc list-inside">
                      <li>Headers: Bond ID, Investor ID, Investor Name, Bond Amount, Whitelisted Wallet, <b>Payment Reference</b></li>
                      <li>All rows must use the same Bond ID</li>
                      <li>Bond Amount numeric only (no commas)</li>
                      <li>Wallet must be at least 8 characters</li>
                      <li><b>Payment Reference is required for each row</b></li>
                      <li>No blank rows</li>
                    </ul>
                  </div>
                </div>
                {isUploading && (
                  <div className="mt-4">
                    <div className="flex items-center mb-2">
                      <Loader className="h-4 w-4 animate-spin text-blue-600 mr-2" />
                      <span className="text-sm text-gray-600">Processing CSV...</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-blue-600 h-2 rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }}></div>
                    </div>
                  </div>
                )}
                {uploadedFile && !isUploading && (
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md flex items-center">
                    <FileText className="h-5 w-5 text-blue-600 mr-2" />
                    <div className="text-sm">
                      <div className="font-medium text-blue-800">{uploadedFile.name}</div>
                      <div className="text-xs text-blue-600">{(uploadedFile.size / 1024).toFixed(1)} KB uploaded</div>
                    </div>
                  </div>
                )}
              </div>

              {csvErrors.length > 0 && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
                  <div className="flex items-start">
                    <AlertTriangle className="h-5 w-5 text-red-600 mr-2 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-red-800 mb-2">CSV Issues</h4>
                      <div className="text-xs text-red-700 space-y-1 max-h-32 overflow-y-auto">
                        {csvErrors.map((err, i) => (<div key={i}>• {err}</div>))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {csvStats && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md text-sm grid grid-cols-2 gap-4">
                  <div>Total Rows: {csvStats.totalRows}</div>
                  <div>Valid Rows: {csvStats.validRows}</div>
                  <div className="col-span-2">Total Tokens: {csvStats.totalAmount.toLocaleString()}</div>
                </div>
              )}

              {showPreview && distributionRows.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Preview</h4>
                  <div className="overflow-x-auto border border-gray-200 rounded-md">
                    <table className="table">
                      <thead className="table-header">
                        <tr>
                          <th className="table-header-cell">Investor ID</th>
                          <th className="table-header-cell">Investor Name</th>
                          <th className="table-header-cell">Bond Amount</th>
                          <th className="table-header-cell">Wallet</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {distributionRows.map((r, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-3 py-2 text-sm text-gray-900">{r.investorId}</td>
                            <td className="px-3 py-2 text-sm text-gray-900">{r.investorName}</td>
                            <td className="px-3 py-2 text-sm text-gray-900 text-right">{r.tokenAmount.toLocaleString()}</td>
                            <td className="px-3 py-2 text-sm text-gray-500 font-mono">{r.wallet}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
              <button
                onClick={downloadTemplate}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                <Download className="h-4 w-4 mr-2" />
                Template
              </button>
              <div className="flex space-x-3">
                <button
                  onClick={() => { setShowUploadModal(false); resetUploadState(); }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >Cancel</button>
                <button
                  onClick={handleConfirmUpload}
                  disabled={!showPreview || !distributionRows.length || csvErrors.length > 0 || isUploading}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {isUploading ? 'Uploading...' : 'Confirm Upload'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Action Modal */}
      {showTransferModal && selectedTransfer && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-medium text-gray-900">
                  {transferAction === 'approve' ? 'Approve Distribution' : 'Reject Distribution'}
                </h3>
                <button
                  onClick={() => setShowTransferModal(false)}
                  className="btn-secondary"
                >
                  <span className="sr-only">Close</span>
                  ×
                </button>
              </div>

              <div className="space-y-4">
                {/* Transfer Details */}
                <div className="border rounded-lg p-4 bg-gray-50">
                  <h4 className="font-medium text-gray-900 mb-3">Transfer Details</h4>
                  <div className="space-y-2 text-sm">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-gray-500">Distribution ID:</span>
                        <span className="ml-2 font-medium">{selectedTransfer.transferId}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Bond:</span>
                        <span className="ml-2 font-medium">{selectedTransfer.bondName}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Amount:</span>
                        <span className="ml-2 font-medium">{selectedTransfer.tokenAmount} tokens</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Value:</span>
                        <span className="ml-2 font-medium">{selectedTransfer.transferValue}</span>
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-500">Reason:</span>
                      <span className="ml-2">{selectedTransfer.reason}</span>
                    </div>
                  </div>
                </div>

                {/* Compliance Checks */}
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-3">Compliance Checks</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Sender Balance:</span>
                      <span className="font-medium">{selectedTransfer.complianceChecks.senderBalance}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Receiver Verification:</span>
                      <span className="font-medium">{selectedTransfer.complianceChecks.receiverKyc}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Transfer Limit:</span>
                      <span className="font-medium">{selectedTransfer.complianceChecks.transferLimit}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Overall Status:</span>
                      <span className={`font-medium ${selectedTransfer.complianceChecks.overallStatus === 'Pass' ? 'text-green-600' : 'text-red-600'}`}>
                        {selectedTransfer.complianceChecks.overallStatus}
                      </span>
                    </div>
                  </div>
                </div>

                {transferAction === 'reject' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Rejection Reason
                    </label>
                    <textarea
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      rows={3}
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      placeholder="Provide detailed reason for rejection..."
                    />
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={() => setShowTransferModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmTransferAction}
                  disabled={transferAction === 'reject' && !rejectionReason.trim()}
                  className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 ${transferAction === 'approve'
                    ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                    : 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                    }`}
                >
                  {transferAction === 'approve' ? 'Approve Distribution' : 'Reject Distribution'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && (
        <div className="fixed inset-0 bg-gray-700 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl mx-4 max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">Transfer Details</h3>
              <button onClick={() => { setShowDetailsModal(false); setDetailsData(null); }} className="text-gray-400 hover:text-gray-600">
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto space-y-6">
              {detailsLoading && (
                <div className="flex items-center text-sm text-gray-600"><Loader className="h-4 w-4 mr-2 animate-spin" /> Loading...</div>
              )}
              {detailsError && (
                <div className="p-4 bg-red-50 border border-red-200 rounded text-sm text-red-700">{detailsError}</div>
              )}
              {detailsData && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div><span className="text-gray-500">Transfer ID:</span> <span className="font-medium ml-1">{detailsData.transferId}</span></div>
                    <div><span className="text-gray-500">Type:</span> <span className="font-medium ml-1">{detailsData.transferType}</span></div>
                    <div><span className="text-gray-500">Status:</span> <span className="font-medium ml-1">{detailsData.status}</span></div>
                    <div><span className="text-gray-500">Created:</span> <span className="ml-1">{formatDate(detailsData.createdAt)}</span></div>
                    <div><span className="text-gray-500">Bond Amount:</span> <span className="ml-1">{detailsData.tokenAmount}</span></div>
                    <div><span className="text-gray-500">Total Value:</span> <span className="ml-1">{detailsData.totalValue}</span></div>
                    <div className="md:col-span-2"><span className="text-gray-500">Description:</span> <span className="ml-1">{detailsData.description || '—'}</span></div>
                  </div>
                  <div className="border-t pt-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Wallets</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm font-mono">
                      <div>
                        <span className="text-gray-500 block">From Wallet</span>
                        <span className="break-all">{detailsData.fromWalletAddress}</span>
                      </div>
                      <div>
                        <span className="text-gray-500 block">To Wallet</span>
                        <span className="break-all">{detailsData.toWalletAddress}</span>
                      </div>
                    </div>
                  </div>
                  {detailsData.transactionHash && (
                    <div className="border-t pt-4">
                      <h4 className="text-sm font-medium text-gray-900 mb-2">Blockchain</h4>
                      <div className="text-sm font-mono break-all">Tx Hash: {detailsData.transactionHash}</div>
                    </div>
                  )}
                  {Array.isArray(detailsData.auditTrail) && detailsData.auditTrail.length > 0 && (
                    <div className="border-t pt-4">
                      <h4 className="text-sm font-medium text-gray-900 mb-2">Audit Trail</h4>
                      <div className="max-h-48 overflow-y-auto text-xs space-y-2">
                        {detailsData.auditTrail.slice().reverse().map((e, idx) => (
                          <div key={idx} className="p-2 rounded bg-gray-50 border border-gray-200">
                            <div className="font-medium text-gray-800">{e.action}</div>
                            <div className="text-gray-600">{new Date(e.performedAt).toLocaleString()}</div>
                            {e.notes && <div className="text-gray-700 mt-1">{e.notes}</div>}
                            {(e.previousStatus || e.newStatus) && (
                              <div className="text-gray-500 mt-1">{e.previousStatus} → {e.newStatus}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => { setShowDetailsModal(false); setDetailsData(null); }}
                className="btn-secondary"
              >Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TokenDistribution;